import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { sentenceCase } from 'change-case';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
// amplify
import { API, graphqlOperation } from "aws-amplify";
import { listCustomers, listPhotographies } from '../graphql/queries';
import { updateCustomer, deleteCustomer } from '../graphql/mutations';
// components
import Label from '../components/label';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'name', label: 'お客様名', alignRight: false },
  { id: 'tel', label: '電話番号', alignRight: false },
  { id: 'address', label: '住所', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query, queryTel) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  console.log(stabilizedThis);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  let result = stabilizedThis.map((el) => el[0]);
  if (query) {
    result = result.filter((_user) =>
      (_user.family_name + _user.first_name).toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  if (queryTel) {
    result = result.filter((_user) =>
      (_user.tel || '').indexOf(queryTel) !== -1
    );
  }
  return result;
}

export default function CustomerPage() {
  const [open, setOpen] = React.useState(null);
  const [page, setPage] = React.useState(0);
  const [order, setOrder] = React.useState('asc');
  const [selected, setSelected] = React.useState([]);
  const [orderBy, setOrderBy] = React.useState('name');
  const [filterName, setFilterName] = React.useState('');
  const [filterTel, setFilterTel] = React.useState('');
  const [rowsPerPage, setRowsPerPage] = React.useState(50);
  const [customers, setCustomers] = React.useState([]);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [editingCustomer, setEditingCustomer] = React.useState(null);
  const [editForm, setEditForm] = React.useState({
    family_name: '', first_name: '', tel: '',
    zipcode: '', prefecture: '', municipality: '', address: '', building: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deletePhotographyCount, setDeletePhotographyCount] = React.useState(0);

  React.useEffect(() => {
    fetchCustomers();
  }, [])
  
  const fetchCustomers = async () => {
    // お客様情報を取得
    const fetchCustomers = await API.graphql(
      graphqlOperation(listCustomers, { limit: 10000})
    );
    // 初回データを保持
    var allCustomers = fetchCustomers.data.listCustomers.items;
    // 続きを取得
    var nextToken = fetchCustomers.data.listCustomers.nextToken;
    while (nextToken != null) {
      const nextPageQuery = await API.graphql(
        graphqlOperation(listCustomers, { limit: 10000, nextToken })
      );
      const nextPageData = nextPageQuery.data;
      allCustomers = allCustomers.concat(nextPageData.listCustomers.items);
      nextToken = nextPageData.listCustomers.nextToken;
    }

    setCustomers(allCustomers)  
  } 

  const handleOpenMenu = (event, customer) => {
    setOpen(event.currentTarget);
    setEditingCustomer(customer);
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const handleOpenEditDialog = () => {
    setEditForm({
      family_name: editingCustomer.family_name || '',
      first_name: editingCustomer.first_name || '',
      tel: editingCustomer.tel || '',
      zipcode: editingCustomer.zipcode || '',
      prefecture: editingCustomer.prefecture || '',
      municipality: editingCustomer.municipality || '',
      address: editingCustomer.address || '',
      building: editingCustomer.building || '',
    });
    setEditDialogOpen(true);
    handleCloseMenu();
  };

  const handleEditFormChange = (field) => (event) => {
    setEditForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveEdit = async () => {
    try {
      await API.graphql(graphqlOperation(updateCustomer, {
        input: { id: editingCustomer.id, ...editForm },
      }));
      setCustomers(customers.map((c) =>
        c.id === editingCustomer.id ? { ...c, ...editForm } : c
      ));
      setEditDialogOpen(false);
      setEditingCustomer(null);
    } catch (e) {
      console.error('updateCustomer error:', e);
    }
  };

  const handleOpenDeleteDialog = async () => {
    handleCloseMenu();
    try {
      const result = await API.graphql(graphqlOperation(listPhotographies, {
        filter: { customerID: { eq: editingCustomer.id } },
        limit: 1,
      }));
      setDeletePhotographyCount(result.data.listPhotographies.items.length);
    } catch (e) {
      console.error('listPhotographies error:', e);
      setDeletePhotographyCount(0);
    }
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.graphql(graphqlOperation(deleteCustomer, {
        input: { id: editingCustomer.id },
      }));
      setCustomers(customers.filter((c) => c.id !== editingCustomer.id));
      setDeleteDialogOpen(false);
      setEditingCustomer(null);
    } catch (e) {
      console.error('deleteCustomer error:', e);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = customers.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const handleFilterByTel = (event) => {
    setPage(0);
    setFilterTel(event.target.value);
  };

  const filteredUsers = applySortFilter(customers, getComparator(order, orderBy), filterName, filterTel);

  const isNotFound = (!filteredUsers.length && !!filterName) || (!filteredUsers.length && !!filterTel);

  return (
    <>
      <Helmet>
        <title> 日光写真館 プリント | お客様情報 </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            お客様一覧
          </Typography>
        </Stack>

        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} filterTel={filterTel} onFilterName={handleFilterByName} onFilterTel={handleFilterByTel} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={customers.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const id = row.id;
                    const name = row.family_name + ' ' + row.first_name;
                    const tel = row.tel;
                    const address = row.prefecture + row.municipality + row.address + (row.building == null ? '' : row.building);
                    const selectedUser = selected.indexOf(name) !== -1;

                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, name)} />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={name} />
                            <Typography variant="subtitle2" noWrap>
                              {name}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              {tel}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography variant="subtitle2" noWrap>
                              {address}
                            </Typography>
                          </Stack>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, row)}>
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={customers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={handleOpenEditDialog}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          編集
        </MenuItem>

        <MenuItem sx={{ color: 'error.main' }} onClick={handleOpenDeleteDialog}>
          <Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />
          削除
        </MenuItem>
      </Popover>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>お客様情報の編集</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField label="姓" fullWidth value={editForm.family_name} onChange={handleEditFormChange('family_name')} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="名" fullWidth value={editForm.first_name} onChange={handleEditFormChange('first_name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="電話番号" fullWidth value={editForm.tel} onChange={handleEditFormChange('tel')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="郵便番号" fullWidth value={editForm.zipcode} onChange={handleEditFormChange('zipcode')} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="都道府県" fullWidth value={editForm.prefecture} onChange={handleEditFormChange('prefecture')} />
            </Grid>
            <Grid item xs={8}>
              <TextField label="市区町村" fullWidth value={editForm.municipality} onChange={handleEditFormChange('municipality')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="番地" fullWidth value={editForm.address} onChange={handleEditFormChange('address')} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="建物名・部屋番号" fullWidth value={editForm.building} onChange={handleEditFormChange('building')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>キャンセル</Button>
          <Button variant="contained" onClick={handleSaveEdit}>保存</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>お客様の削除</DialogTitle>
        <DialogContent>
          {deletePhotographyCount === 0 && (
            <Typography>
              {editingCustomer && `${editingCustomer.family_name} ${editingCustomer.first_name}`}さんを削除しますか？
            </Typography>
          )}
          {deletePhotographyCount > 0 ? (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              このお客様には撮影記録が残っているため、削除できません。
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              この操作は元に戻せません。
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>キャンセル</Button>
          {deletePhotographyCount === 0 && (
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>OK</Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
